/**
 * React wrapper that renders the merged trace-route graph for all agents
 * inside a single pannable / zoomable container for the "Path Details" tab.
 *
 * Nodes shared between agents (same IP in detailed mode, same organization
 * in summary mode) are merged into one visual node — see WebPathDetailGraphHelper.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import useIsPrinting from '../../../utilities/PrintUtilities';
import useWebPathDetailGraphStyles from './WebPathDetailGraphStyles';
import drawPathDetailGraph from './WebPathDetailGraphHelper';

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.1;

export default function WebPathDetailGraph({ agents, agentsById, detailLevel, onNodeClick, graphData }) {
    const classes = useWebPathDetailGraphStyles();
    const isPrinting = useIsPrinting();
    const outerRef = useRef();
    const svgRef = useRef();

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [dragOrigin, setDragOrigin] = useState({ x: 0, y: 0 });

    // Draw the merged graph whenever inputs change
    useEffect(() => {
        if (svgRef.current) {
            drawPathDetailGraph(svgRef.current, agents || [], agentsById, detailLevel, onNodeClick, graphData);
        }
    }, [agents, agentsById, detailLevel, onNodeClick, graphData]);

    // Reset zoom & pan when detail level changes
    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [detailLevel]);

    // ── Zoom buttons ──
    const handleZoomIn = useCallback(() => {
        setZoom((prev) => Math.min(MAX_ZOOM, Number((prev + ZOOM_STEP).toFixed(2))));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoom((prev) => Math.max(MIN_ZOOM, Number((prev - ZOOM_STEP).toFixed(2))));
    }, []);

    // ── Mouse wheel zoom ──
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            setZoom((prev) => Math.min(MAX_ZOOM, Number((prev + ZOOM_STEP).toFixed(2))));
        } else {
            setZoom((prev) => Math.max(MIN_ZOOM, Number((prev - ZOOM_STEP).toFixed(2))));
        }
    }, []);

    // ── Drag to pan ──
    const handleMouseDown = useCallback(
        (e) => {
            if (e.button !== 0) return;
            const interactive = e.target?.closest?.('[data-path-interactive="true"]');
            if (interactive) return;
            e.preventDefault();
            setDragging(true);
            setDragOrigin({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        },
        [pan]
    );

    const handleMouseMove = useCallback(
        (e) => {
            if (!dragging) return;
            setPan({ x: e.clientX - dragOrigin.x, y: e.clientY - dragOrigin.y });
        },
        [dragging, dragOrigin]
    );

    const handleMouseUp = useCallback(() => {
        if (dragging) setDragging(false);
    }, [dragging]);

    // Attach wheel listener with { passive: false } so we can preventDefault
    useEffect(() => {
        const el = outerRef.current;
        if (!el) return undefined;
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);

    return (
        <div className={classes.pathDetailWrapper}>
            {/* Zoom controls */}
            {!isPrinting && (
                <div className={classes.zoomControls}>
                    <button type="button" className={classes.zoomButton} onClick={handleZoomIn}>
                        +
                    </button>
                    <button type="button" className={classes.zoomButton} onClick={handleZoomOut}>
                        −
                    </button>
                </div>
            )}

            {/* Pannable / zoomable viewport */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
                ref={outerRef}
                className={classes.viewport}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <div
                    style={{
                        transformOrigin: 'left top',
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        width: 'max-content',
                        userSelect: 'none',
                        cursor: dragging ? 'move' : 'default',
                    }}
                >
                    {(agents || []).length > 0 && (
                        <div className={classes.graphContainer}>
                            <svg ref={svgRef} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
